import React, { useState, useEffect, useRef } from 'react'
import {
  X, Save, User, Mail, Phone, Calendar, MapPin,
  School, Bus, Car, Clock, Camera, Link, Globe, Briefcase, Home
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import { extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

interface School {
  id: string
  name: string
  district_id?: string
  school_type?: string
}

interface Place {
  id: string
  name: string
  place_type: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string
}

interface UserData {
  id: string
  username: string
  display_name: string
  email?: string
  phone_number?: string
  birthdate?: string
  avatar_url?: string
  color_theme: string
  is_parent: boolean
  login_enabled?: boolean
  school_id?: string
  school_name?: string
  primary_place_id?: string
  primary_place_name?: string
  transport_mode?: string
  bus_number?: string
  bus_pickup_time?: string
  bus_dropoff_time?: string
  car_pickup_time?: string
  car_dropoff_time?: string
  school_start_time?: string
  school_end_time?: string
  special_friday_arrangement?: string
  notes?: string
  oauth_accounts?: any[]
  profile_data?: any
  google_email?: string
  work_start_time?: string
  work_end_time?: string
  work_days?: number[]
  work_location?: string
  work_from_home?: boolean
  work_schedule_notes?: string
}

interface UserEditorCompleteProps {
  userId: string
  userData: UserData
  onClose: () => void
  onSave: () => void
}

// HTML5 <input type="time"> only accepts "HH:mm" or "HH:mm:ss".
// Phoenix/Postgrex serializes %Time{} as "17:00:00.000000" (microseconds),
// which the browser silently rejects — the input renders blank and the
// user thinks the value didn't save.
const toTimeInput = (v: string | null | undefined): string => {
  if (!v) return ''
  const s = String(v)
  if (s.length === 5) return s
  return s.slice(0, 5)
}

export const UserEditorComplete: React.FC<UserEditorCompleteProps> = ({
  userId,
  userData,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<UserData>(userData)
  const [schools, setSchools] = useState<School[]>([])
  const [places, setPlaces] = useState<Place[]>([])
  const [loading, setLoading] = useState(false)
  const [showPlaceForm, setShowPlaceForm] = useState(false)
  const [newPlace, setNewPlace] = useState({
    name: '',
    place_type: 'home',
    street_address: '',
    city: '',
    state: 'GA',
    zip_code: '',
    phone_number: '',
    notes: ''
  })
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadDropdownData()
    loadUserDetails()
    // Initialize oauth_accounts from userData if available
    if (userData.google_linked) {
      // If we know they have Google linked, create a placeholder entry
      setFormData(prev => ({
        ...prev,
        oauth_accounts: userData.oauth_accounts || [
          { email: userData.google_email || 'Google Account', linked_at: null }
        ]
      }))
    }
    // Merge profile data if exists
    if (userData.profile_data) {
      setFormData(prev => ({
        ...prev,
        google_email: userData.profile_data.google_email || prev.google_email,
        avatar_url: userData.profile_data.avatar_url || prev.avatar_url
      }))
    }
  }, [userData])

  const loadDropdownData = async () => {
    try {
      const [schoolsData, placesData] = await Promise.all([
        apiClient.get('/api/schools/list'),
        apiClient.get('/api/places/list')
      ])
      setSchools(schoolsData || [])
      setPlaces(placesData || [])
    } catch (error) {
      console.error('Failed to load dropdown data:', error)
    }
  }

  const loadUserDetails = async () => {
    try {
      // Try to load OAuth accounts - this endpoint might not exist yet
      // For now, we'll rely on the google_linked flag from the user data
      // const oauthData = await apiClient.get(`/api/users/${userId}/oauth-accounts`)
      // if (oauthData) {
      //   setFormData(prev => ({
      //     ...prev,
      //     oauth_accounts: oauthData
      //   }))
      // }
    } catch (error) {
      console.error('Failed to load user details:', error)
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await apiClient.post(`/api/users/${userId}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      setFormData(prev => ({ ...prev, avatar_url: response.avatar_url }))
      toast.success('Avatar uploaded successfully')
    } catch (error) {
      toast.error('Failed to upload avatar')
    }
  }

  const handleCreatePlace = async () => {
    try {
      const response = await apiClient.post('/api/places', newPlace)
      toast.success('Place created successfully')
      setShowPlaceForm(false)
      setNewPlace({
        name: '', place_type: 'home', street_address: '',
        city: '', state: 'GA', zip_code: '', phone_number: '', notes: ''
      })
      loadDropdownData()
      // Set the new place as selected
      if (response.place_id) {
        setFormData(prev => ({ ...prev, primary_place_id: response.place_id }))
      }
    } catch (error) {
      toast.error('Failed to create place')
    }
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      // Prepare data for backend - pass through OAuth action if set
      const updateData = { ...formData }
      
      await apiClient.put(`/api/users/${userId}`, updateData)
      toast.success('User updated successfully')
      
      // Clear OAuth action after save
      setFormData(prev => {
        const { oauth_action, oauth_email, ...rest } = prev
        return rest
      })
      
      onSave()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to update user')
      // Clear OAuth action on error too
      setFormData(prev => {
        const { oauth_action, oauth_email, ...rest } = prev
        return rest
      })
    } finally {
      setLoading(false)
    }
  }

  const calculateAge = (birthdate: string) => {
    if (!birthdate) return null
    const today = new Date()
    const birth = new Date(birthdate + 'T12:00:00')
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">Edit User</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start space-x-6">
              <div className="flex flex-col items-center">
                <div 
                  className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ 
                    backgroundColor: formData.avatar_url ? 'transparent' : formData.color_theme 
                  }}
                >
                  {formData.avatar_url ? (
                    <img src={formData.avatar_url} alt={formData.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl text-white font-bold">
                      {formData.display_name?.[0] || 'U'}
                    </span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center"
                >
                  <Camera className="w-4 h-4 mr-1" />
                  Change Photo
                </button>
              </div>

              <div className="flex-1 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number || ''}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="555-555-5555"
                  />
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">Personal Details</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birthdate
                  </label>
                  <input
                    type="date"
                    value={formData.birthdate ? extractDateInTimezoneSync(formData.birthdate) : ''}
                    onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {formData.birthdate && (
                    <p className="text-xs text-gray-500 mt-1">
                      Age: {calculateAge(formData.birthdate)} years
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color Theme
                  </label>
                  <input
                    type="color"
                    value={formData.color_theme}
                    onChange={(e) => setFormData({ ...formData, color_theme: e.target.value })}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_parent}
                      onChange={(e) => setFormData({ ...formData, is_parent: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Parent/Admin</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.login_enabled !== false}
                      onChange={(e) => setFormData({ ...formData, login_enabled: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Can Login</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Google Account Linking */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">Google Account Linking</h3>
              
              {/* Display existing OAuth accounts */}
              {formData.oauth_accounts && formData.oauth_accounts.length > 0 ? (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Linked Google Accounts:</p>
                  <div className="space-y-2">
                    {formData.oauth_accounts.map((account, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Globe className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-gray-900">{account.email}</p>
                            <p className="text-xs text-gray-500">
                              Linked on {account.linked_at ? new Date(account.linked_at).toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (confirm(`Unlink ${account.email} from this account?`)) {
                              setFormData({ 
                                ...formData, 
                                oauth_action: 'unlink',
                                oauth_email: account.email 
                              })
                            }
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Unlink
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-600">No Google accounts linked</p>
                </div>
              )}
              
              {/* Add new OAuth account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Additional Google Account
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="email"
                    placeholder="user@gmail.com"
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const email = (e.target as HTMLInputElement).value.trim()
                        if (email && email.includes('@')) {
                          setFormData({ 
                            ...formData, 
                            oauth_action: 'link',
                            oauth_email: email
                          })
                          ;(e.target as HTMLInputElement).value = ''
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.querySelector('input[placeholder="user@gmail.com"]') as HTMLInputElement
                      const email = input?.value.trim()
                      if (email && email.includes('@')) {
                        setFormData({ 
                          ...formData, 
                          oauth_action: 'link',
                          oauth_email: email
                        })
                        input.value = ''
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Link Account
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Note: The user will need to authenticate with this Google account when logging in
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Primary Location</h3>
                <button
                  onClick={() => setShowPlaceForm(!showPlaceForm)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add New Place
                </button>
              </div>
              
              {showPlaceForm && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Place Name"
                      value={newPlace.name}
                      onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <select
                      value={newPlace.place_type}
                      onChange={(e) => setNewPlace({ ...newPlace, place_type: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="home">Home</option>
                      <option value="work">Work</option>
                      <option value="school">School</option>
                      <option value="medical">Medical</option>
                      <option value="recreation">Recreation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={newPlace.street_address}
                    onChange={(e) => setNewPlace({ ...newPlace, street_address: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="City"
                      value={newPlace.city}
                      onChange={(e) => setNewPlace({ ...newPlace, city: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={newPlace.state}
                      onChange={(e) => setNewPlace({ ...newPlace, state: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="ZIP"
                      value={newPlace.zip_code}
                      onChange={(e) => setNewPlace({ ...newPlace, zip_code: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowPlaceForm(false)}
                      className="px-3 py-1 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreatePlace}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Create Place
                    </button>
                  </div>
                </div>
              )}

              <select
                value={formData.primary_place_id || ''}
                onChange={(e) => setFormData({ ...formData, primary_place_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No Primary Location</option>
                {places.map(place => (
                  <option key={place.id} value={place.id}>
                    {place.name} ({place.place_type})
                    {place.city && ` - ${place.city}, ${place.state}`}
                  </option>
                ))}
              </select>
            </div>

            {/* School & Transportation (for kids) */}
            {!formData.is_parent && (
              <>
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4">School Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        School
                      </label>
                      <select
                        value={formData.school_id || ''}
                        onChange={(e) => setFormData({ ...formData, school_id: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No School</option>
                        {schools.map(school => (
                          <option key={school.id} value={school.id}>
                            {school.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Grade
                      </label>
                      <input
                        type="text"
                        value={formData.grade || ''}
                        onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 8th Grade"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        School Start Time
                      </label>
                      <input
                        type="time"
                        value={toTimeInput(formData.school_start_time)}
                        onChange={(e) => setFormData({ ...formData, school_start_time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        School End Time
                      </label>
                      <input
                        type="time"
                        value={toTimeInput(formData.school_end_time)}
                        onChange={(e) => setFormData({ ...formData, school_end_time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4">Transportation</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transportation Mode
                      </label>
                      <select
                        value={formData.transport_mode || ''}
                        onChange={(e) => setFormData({ ...formData, transport_mode: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Set</option>
                        <option value="bus">School Bus</option>
                        <option value="parent_car">Parent Car</option>
                        <option value="drives_self">Drives Self</option>
                        <option value="friend_pickup">Friend Pickup</option>
                        <option value="mixed">Mixed (varies by day)</option>
                      </select>
                    </div>

                    {formData.transport_mode === 'bus' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bus Number
                          </label>
                          <input
                            type="text"
                            value={formData.bus_number || ''}
                            onChange={(e) => setFormData({ ...formData, bus_number: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bus Pickup Time
                          </label>
                          <input
                            type="time"
                            value={toTimeInput(formData.bus_pickup_time)}
                            onChange={(e) => setFormData({ ...formData, bus_pickup_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bus Dropoff Time
                          </label>
                          <input
                            type="time"
                            value={toTimeInput(formData.bus_dropoff_time)}
                            onChange={(e) => setFormData({ ...formData, bus_dropoff_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </>
                    )}

                    {formData.transport_mode === 'parent_car' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Car Pickup Time
                          </label>
                          <input
                            type="time"
                            value={toTimeInput(formData.car_pickup_time)}
                            onChange={(e) => setFormData({ ...formData, car_pickup_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Car Dropoff Time
                          </label>
                          <input
                            type="time"
                            value={toTimeInput(formData.car_dropoff_time)}
                            onChange={(e) => setFormData({ ...formData, car_dropoff_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </>
                    )}

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Friday Special Arrangement
                      </label>
                      <input
                        type="text"
                        value={formData.special_friday_arrangement || ''}
                        onChange={(e) => setFormData({ ...formData, special_friday_arrangement: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Early pickup for sports"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Work Schedule (optional for anyone — parents usually, kids with jobs too) */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-1 flex items-center">
                <Briefcase className="w-5 h-5 mr-2" />
                Work Schedule
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Optional. The kiosk uses this to auto-show "At Work" or "Working from home"
                during the hours below — no manual overrides needed each morning.
              </p>

              <div className="mb-4 flex items-center">
                <input
                  id="work_from_home"
                  type="checkbox"
                  checked={!!formData.work_from_home}
                  onChange={(e) => setFormData({ ...formData, work_from_home: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="work_from_home" className="ml-2 text-sm text-gray-700 flex items-center">
                  <Home className="w-4 h-4 mr-1" />
                  Works from home (kiosk stays green with a briefcase badge)
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { n: 1, label: 'Mon' },
                    { n: 2, label: 'Tue' },
                    { n: 3, label: 'Wed' },
                    { n: 4, label: 'Thu' },
                    { n: 5, label: 'Fri' },
                    { n: 6, label: 'Sat' },
                    { n: 7, label: 'Sun' },
                  ].map(({ n, label }) => {
                    const days = formData.work_days ?? [1, 2, 3, 4, 5]
                    const active = days.includes(n)
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => {
                          const current = formData.work_days ?? [1, 2, 3, 4, 5]
                          const next = active
                            ? current.filter((d) => d !== n)
                            : [...current, n].sort((a, b) => a - b)
                          setFormData({ ...formData, work_days: next })
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                          active
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Start Time
                  </label>
                  <input
                    type="time"
                    value={toTimeInput(formData.work_start_time)}
                    onChange={(e) => setFormData({ ...formData, work_start_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work End Time
                  </label>
                  <input
                    type="time"
                    value={toTimeInput(formData.work_end_time)}
                    onChange={(e) => setFormData({ ...formData, work_end_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Work Location
                </label>
                <input
                  type="text"
                  value={formData.work_location || ''}
                  onChange={(e) => setFormData({ ...formData, work_location: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Home office, Downtown office, Chick-Fil-A"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Notes
                </label>
                <textarea
                  value={formData.work_schedule_notes || ''}
                  onChange={(e) => setFormData({ ...formData, work_schedule_notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Optional — e.g., back-to-back meetings Tuesdays"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}